import React from "react";
import { AlertCircle, ArrowUp, CheckCircle2, Info } from "lucide-react";

import { Callout } from "@/components/ui/Callout";
import Skeleton from "@/components/shared/Skeleton";

import { DETAIL_LIST, TONE } from "../constants";
import Chip from "./Chip";
import DetailDrawer from "./DetailDrawer";
import RollbackHazard from "./RollbackHazard";
import ScopedTableList from "./ScopedTableList";

// One fact per cell. Kept small and derived — every number here is computed on
// the server from the frozen policy set that version shipped with, so it cannot
// disagree with the SQL an Apply would run.
const Fact = ({ label, value, title }) => (
    <div className="rounded-md border border-border-primary px-2.5 py-2" title={title}>
        <div className="text-[10px] uppercase tracking-wide text-text-secondary">
            {label}
        </div>
        <div className="text-[13px] font-medium text-text-primary mt-0.5 font-mono">
            {value}
        </div>
    </div>
);

// Where this database sits against the code, in words. `behind` and `ahead` are
// both differences from the code, and only one of them is fixed by pressing
// Apply — so they never share a chip.
const POSITION = {
    current: {
        tone: TONE.emerald,
        icon: <CheckCircle2 size={11} />,
        label: "matches the code",
    },
    behind: {
        tone: TONE.amber,
        icon: <AlertCircle size={11} />,
        label: "behind the code",
    },
    ahead: {
        tone: TONE.violet,
        icon: <ArrowUp size={11} />,
        label: "ahead of the code",
    },
    unknown: {
        tone: TONE.slate,
        icon: <AlertCircle size={11} />,
        label: "not recorded",
    },
};

const formatStamp = (iso) => {
    if (!iso) return null;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
};

/**
 * What the row-security version on one database actually is.
 *
 * The column can only hold "v16". This is where that number becomes a decision:
 * what the version added, how its policies reach a tenant_id, when it landed on
 * this database, where it sits against the code, and — for every older version
 * — what a rollback to it would leave broken.
 *
 * The timeline is the whole archive rather than only the versions below this
 * database's, because "we are two versions behind" is exactly the case where a
 * reader wants to see what the two are.
 */
const RlsVersionDrawer = ({ row, catalog, loading, onClose }) => {
    const version = row?.rlsCurrent ?? null;
    const entry = catalog?.rlsVersion(version);
    const codeVersion = row?.rlsCodeVersion ?? catalog?.rlsCurrent ?? null;
    const position = POSITION[row?.rlsPosition] ?? POSITION.unknown;
    const appliedAt = formatStamp(row?.rlsAppliedAt);

    return (
        <DetailDrawer
            open={!!row}
            onClose={onClose}
            title={
                version == null
                    ? "Row security — no version recorded"
                    : `Row security v${version}${entry ? ` — ${entry.title}` : ""}`
            }
            subtitle={row?.label}
        >
            {loading && <Skeleton className="h-24 w-full" />}

            {!loading && version == null && (
                <Callout tone="warning" icon={AlertCircle} size="xs">
                    <span className="font-medium block mb-0.5">
                        This database has no row-security version
                    </span>
                    Either the policies have never been applied through this screen,
                    or the <span className="font-mono">rls_meta</span> table could not
                    be read. Apply row security to record one.
                </Callout>
            )}

            {!loading && version != null && (
                <>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Chip
                            tone={position.tone}
                            icon={position.icon}
                            label={position.label}
                        />
                        {codeVersion != null && (
                            <span className="text-[11px] text-text-secondary">
                                the code is on{" "}
                                <span className="font-mono">v{codeVersion}</span>
                            </span>
                        )}
                    </div>

                    {!entry && (
                        <Callout tone="warning" icon={AlertCircle} size="xs">
                            <span className="font-medium block mb-0.5">
                                v{version} is not in the code archive
                            </span>
                            This build cannot describe it, and cannot re-apply it. It
                            is either older than the archive keeps or from a build this
                            server is not running.
                        </Callout>
                    )}

                    {entry && (
                        <>
                            <div className="text-[12px] text-text-secondary leading-relaxed">
                                {entry.summary}
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                <Fact
                                    label="Tables policed"
                                    value={entry.totalTablesPoliced}
                                    title="Tables this version creates policies for"
                                />
                                <Fact
                                    label="Statements"
                                    value={entry.statementCount}
                                    title="SQL statements an apply of this version runs"
                                />
                                <Fact
                                    label="Applied"
                                    value={appliedAt ?? "not recorded"}
                                    title="When this version landed on this database"
                                />
                                <Fact
                                    label="Migration"
                                    value={entry.relatedMigration ?? "—"}
                                    title={
                                        entry.relatedMigration
                                            ? "The schema revision this version pairs with"
                                            : "This version pairs with no particular migration"
                                    }
                                />
                            </div>

                            {entry.renumberedFrom && (
                                <Callout tone="neutral" icon={Info} size="xs">
                                    <span className="font-medium block mb-0.5">
                                        This version was renumbered
                                    </span>
                                    {entry.renumberedFrom}
                                </Callout>
                            )}

                            {entry.note && (
                                <Callout tone="neutral" icon={Info} size="xs">
                                    {entry.note}
                                </Callout>
                            )}

                            {entry.addedTables?.length > 0 && (
                                <div>
                                    <div className="text-xs font-medium text-text-secondary mb-2">
                                        What v{entry.version} added
                                    </div>
                                    <ScopedTableList tables={entry.addedTables} />
                                </div>
                            )}

                            {entry.removedTables?.length > 0 && (
                                <div>
                                    <div className="text-xs font-medium text-text-secondary mb-2">
                                        No longer policed at v{entry.version}
                                    </div>
                                    <ScopedTableList tables={entry.removedTables} />
                                </div>
                            )}

                            {entry.exemptNote && (
                                <Callout tone="neutral" icon={Info} size="xs">
                                    {entry.exemptNote}
                                </Callout>
                            )}

                            <RollbackHazard entry={entry} />
                        </>
                    )}

                    {catalog?.rlsCatalog?.length > 0 && (
                        <div>
                            <div className="text-xs font-medium text-text-secondary mb-2">
                                Every version in the archive
                            </div>
                            <div className={DETAIL_LIST}>
                                {[...catalog.rlsCatalog]
                                    .sort((a, b) => b.version - a.version)
                                    .map((item) => {
                                        const isThis = item.version === version;
                                        return (
                                            <div
                                                key={item.version}
                                                className={`px-3 py-2 flex items-start gap-3 ${
                                                    isThis ? "bg-background-icon/30" : ""
                                                }`}
                                            >
                                                <span className="font-mono text-[12px] text-text-primary w-8 shrink-0">
                                                    v{item.version}
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-[12px] text-text-primary">
                                                        {item.title}
                                                    </div>
                                                    <div className="text-[11px] text-text-secondary mt-0.5">
                                                        {item.totalTablesPoliced} tables
                                                        {item.addedTables?.length
                                                            ? ` · added ${item.addedTables
                                                                  .map((t) => t.name)
                                                                  .join(", ")}`
                                                            : ""}
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-1 shrink-0">
                                                    {isThis && (
                                                        <Chip
                                                            tone={TONE.violet}
                                                            label="on this database"
                                                        />
                                                    )}
                                                    {item.isCurrent && (
                                                        <Chip
                                                            tone={TONE.emerald}
                                                            label="the code's version"
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    )}
                </>
            )}
        </DetailDrawer>
    );
};

export default RlsVersionDrawer;
